/**
 * API Client
 *
 * Axios instance configured with:
 * - Base URL from environment
 * - Auth token interceptor
 * - Token refresh on 401
 * - Error handling
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// API base URL - use same hostname as the browser for mobile access
function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;

  // Empty string means same-origin (nginx proxies /api to backend)
  if (envUrl === '') {
    return '';
  }

  // If explicitly set and not localhost, use it
  if (envUrl && !envUrl.includes('localhost')) {
    return envUrl;
  }

  // For development: use the same hostname the browser is on, with port 3001
  // This allows mobile devices on the same network to access the API
  const hostname = window.location.hostname;
  return `http://${hostname}:3001`;
}

const API_URL = getApiUrl();

// Create Axios instance
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================================================
// Token Storage
// =============================================================================

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const CURRENT_ORG_KEY = 'currentOrgSlug';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// =============================================================================
// Organization Context Storage
// =============================================================================

export function getCurrentOrgSlug(): string | null {
  return localStorage.getItem(CURRENT_ORG_KEY);
}

// =============================================================================
// Request Interceptor - Attach Auth Token and Org Context
// =============================================================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add organization context header
    const orgSlug = getCurrentOrgSlug();
    if (orgSlug) {
      config.headers['X-Organization-Slug'] = orgSlug;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor - Handle 401 and Refresh Token
// =============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject: (err: Error) => {
            reject(err);
          },
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      // Try to refresh the token
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data;
      setTokens(accessToken, newRefreshToken);

      // Update authorization header
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;

      // Process queued requests
      processQueue(null, accessToken);

      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear tokens and redirect to login
      processQueue(refreshError as Error, null);
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// =============================================================================
// API Error Type
// =============================================================================

export interface ApiError {
  error: string;
  message: string;
  details?: Array<{ field: string; message: string }>;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error) && error.response?.data?.message) {
    const { message, details } = error.response.data;
    // Validation failures carry per-field detail. Surfacing it turns the
    // otherwise undiagnosable "Invalid request data" into something a user
    // (or a bug report) can act on.
    if (details?.length) {
      const fields = details
        .slice(0, 3)
        .map((d) => `${d.field.replace(/^(body|query|params)\./, '')}: ${d.message}`)
        .join('; ');
      const more = details.length > 3 ? ` (+${details.length - 3} more)` : '';
      return `${message} — ${fields}${more}`;
    }
    return message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
